import type {TurnResponse} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {produce} from 'immer';

import {parseTurnTitle} from '../components/WBAgent/threadTitle';
import {terminalEventForTurnState, TurnEventExtractor} from './runtime/turn';
import {applyRuntimeEventToThread} from './store/eventReducer';
import type {Thread} from './store/thread';
import {createEmptyThread} from './store/thread';

/**
 * buildThreadFromTurns reconstructs a `Thread` from a chronologically-sorted
 * array of `TurnResponse`s. All turns are replayed through the event pipeline
 * (`TurnEventExtractor` + `applyRuntimeEventToThread`) in a single Immer
 * produce call, producing an identical `MessageTree` to what live streaming
 * would create.
 */
export function buildThreadFromTurns(
  rootTurnId: string,
  turns: TurnResponse[]
): Thread {
  return produce(createEmptyThread(rootTurnId), draft => {
    for (const turn of turns) {
      const extractor = new TurnEventExtractor(turn.id);
      const events = extractor.extract(turn);
      const now = new Date(turn.created_at).getTime();

      applyRuntimeEventToThread(
        draft,
        {type: 'run-started', runId: turn.id, threadId: rootTurnId},
        now
      );

      for (const event of events) {
        applyRuntimeEventToThread(draft, event, now);
      }

      const terminalEvent = terminalEventForTurnState(
        turn.state,
        turn.id,
        turn.error_info
      );
      if (terminalEvent) {
        applyRuntimeEventToThread(draft, terminalEvent, now);
      }
    }

    draft.rootTurnId = rootTurnId;

    // When a turn was cancelled before the backend recorded any messages,
    // store a descriptive error so the UI can explain why the thread is empty.
    const lastTurn = turns.length > 0 ? turns[turns.length - 1] : null;
    if (
      draft.messageTree.rootChildren.length === 0 &&
      lastTurn?.state === 'cancelled'
    ) {
      draft.error =
        'This conversation was cancelled before the agent could respond.';
    }

    if (turns.length > 0) {
      const first = turns[0];
      const last = turns[turns.length - 1];

      const {title, promptSnippet} = parseTurnTitle(first);
      draft.title = title;
      draft.promptSnippet = promptSnippet;
      draft.createdAt = new Date(first.created_at).getTime();
      draft.updatedAt = new Date(last.updated_at).getTime();
      draft.serverUpdatedAt = new Date(first.updated_at).getTime();
      draft.projects = extractProjectsForThread(first);
    }
  });
}

/**
 * extractProjectsForThread returns the projects relevant to a thread.
 *
 * NOTE: The legacy turn-level entity/project fields are deprecated. Once
 * turns drop them, this should parse user-prompt parts instead.
 */
export function extractProjectsForThread(
  turn: TurnResponse
): Array<{entityName: string; projectName: string}> {
  if (!turn.wandb_entity || !turn.wandb_project) {
    return [];
  }

  return [{entityName: turn.wandb_entity, projectName: turn.wandb_project}];
}
